/* global Promise */
/* global fetch */

import Url from "node:url";
import { SCHEMA_VERSION } from "./constants";
import { DocumentError, ERRORCODES } from "./errors";

const v3ApiPath = "/api/v3";

export const DocumentTypes = {
  HOYLU: "HOYLU",
  SKETCH: "SKETCH",
  FLOW: "FLOW",
  EXPERIMENTAL: "EXPERIMENTAL",
};

export const Permissions = {
  Read: "Read",
  Write: "Write",
};

type Credential = {
  token: string;
};

type CredentialProvider = () => Credential;

type Config = {
  server: string;
  timeout?: number;
  credentialProvider: CredentialProvider;
};

export interface GetAutoSubscribeToContainerResponse {
  containerId: string;
  containerName: string;
}

export class DocumentService {
  private server: string;
  private timeout: number;
  private credentialProvider: CredentialProvider;

  constructor(config: Config) {
    config = config || {};

    if (!config.server)
      throw new DocumentError("Missing 'server'", ERRORCODES.MISSING_DATA);
    if (!config.credentialProvider)
      throw new DocumentError(
        "Missing 'credentialProvider'",
        ERRORCODES.MISSING_DATA,
      );

    const u = Url.parse(config.server);
    if (!u.host)
      throw new DocumentError("Invalid 'server'", ERRORCODES.INVALID_DATA);

    this.server = Url.format(u);
    this.timeout = config.timeout || 15000;
    this.credentialProvider = config.credentialProvider;

    if (
      this.timeout === null ||
      Number.isNaN(this.timeout) ||
      this.timeout < 0
    ) {
      this.timeout = 15000;
    }
  }

  setTokenProvider(credentialProvider: CredentialProvider) {
    this.credentialProvider = credentialProvider;
  }

  private getToken() {
    if (this.credentialProvider) {
      const credentials = this.credentialProvider();
      if (credentials) {
        return credentials.token;
      }
    }
    return undefined;
  }

  async getWorkspaceMetaData(id: string) {
    validateDocumentId(id);

    const url = Url.resolve(this.server, `${v3ApiPath}/workspace/${id}`);

    const credentials = this.getToken();
    const headers = credentials
      ? { authorization: `Bearer ${credentials}` }
      : undefined;

    const options: RequestInit = {
      method: "GET",
      mode: "cors",
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };

    const response = await fetch(url, options);
    return handleResponse(id)(response);
  }

  hasPassword(id: string) {
    validateDocumentId(id);

    const url = Url.resolve(
      this.server,
      `${v3ApiPath}/workspace/${id}/password`,
    );

    const options: any = {
      method: "HEAD",
      timeout: this.timeout,
      mode: "cors",
      headers: {
        authorization: undefined,
      },
    };

    const credentials = this.getToken();
    if (credentials) {
      options.headers.authorization = `Bearer ${credentials}`;
    }
    return fetch(url, options as RequestInit).then((response) => {
      if (response.status === 200) {
        return true;
      } else if (response.status === 204) {
        return false;
      } else if (
        response.status === ERRORCODES.REQUEST_ERROR ||
        response.status === ERRORCODES.NOT_AUTHORIZED ||
        response.status === ERRORCODES.FORBIDDEN ||
        response.status === ERRORCODES.SERVER_ERROR
      ) {
        return Promise.reject(
          new DocumentError(
            `Server: ${response.status} unable test password for document`,
            response.status,
          ),
        );
      } else {
        return Promise.reject(
          new DocumentError(
            `Server returned ${response.status} for id: ${id}`,
            ERRORCODES.SERVER_ERROR,
          ),
        );
      }
    });
  }

  async getAutoSubscribeToContainer(
    id: string,
  ): Promise<GetAutoSubscribeToContainerResponse | undefined> {
    validateDocumentId(id);

    const url = Url.resolve(
      this.server,
      `${v3ApiPath}/workspace/${id}/autoSubscribeToContainer`,
    );

    const credentials = this.getToken();
    const headers = credentials
      ? { authorization: `Bearer ${credentials}` }
      : undefined;

    const options: RequestInit = {
      method: "GET",
      mode: "cors",
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };

    const response = await fetch(url, options);
    if (!response.ok) {
      return handleResponse(id)(response);
    }

    if (response.status === 204) {
      return undefined; // No content, user is not going to be auto-subscribed to a container
    }

    const body: unknown = await response.json();
    if (!isGetAutoSubscribeToContainerResponse(body)) {
      throw new DocumentError(
        "Missing data in response",
        ERRORCODES.MISSING_DATA,
      );
    }

    return body;
  }

  connect(id: string, password?: string) {
    validateDocumentId(id);
    const url = new URL(
      Url.resolve(this.server, `${v3ApiPath}/workspace/${id}/open`),
    );
    url.searchParams.set(
      "maxSupportedSchemaVersion",
      SCHEMA_VERSION.toString(),
    );

    const options: any = {
      method: "GET",
      timeout: this.timeout,
      mode: "cors",
      headers: {
        authorization: undefined,
      },
    };

    const credentials = this.getToken();
    if (credentials) {
      options.headers.authorization = `Bearer ${credentials}`;
    }

    if (password && typeof password === "string") {
      options.headers["X-Hoylu-Workspace-Password"] = password;
    }

    return fetch(url.toString(), options as RequestInit)
      .then((response) => {
        if (response.status === 200) {
          // could throw parse error here
          return response.json();
        } else if (
          response.status === ERRORCODES.REQUEST_ERROR ||
          response.status === ERRORCODES.NOT_AUTHORIZED ||
          response.status === ERRORCODES.FORBIDDEN ||
          response.status === ERRORCODES.NOT_FOUND ||
          response.status === ERRORCODES.PRECONDITION_FAILED ||
          response.status === ERRORCODES.SERVER_ERROR
        ) {
          return Promise.reject(
            new DocumentError(`${response.status} id: ${id}`, response.status),
          );
        } else {
          return Promise.reject(
            new DocumentError(
              `Server returned ${response.status} for id: ${id}`,
              ERRORCODES.SERVER_ERROR,
            ),
          );
        }
      })
      .then((body) => {
        if (!body?.workspaceId) {
          Promise.reject(
            new DocumentError(
              "Missing data in response",
              ERRORCODES.MISSING_DATA,
            ),
          );
        }
        return body;
      });
  }

  async updateSchemaVersion(
    id: string,
    schemaVersion: number = SCHEMA_VERSION,
  ) {
    validateDocumentId(id);
    const url = new URL(
      Url.resolve(this.server, `${v3ApiPath}/workspace/${id}/schemaVersion`),
    );

    const credentials = this.getToken();
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (credentials) {
      headers.authorization = `Bearer ${credentials}`;
    }

    const options: RequestInit = {
      method: "PATCH",
      mode: "cors",
      headers,
      body: JSON.stringify(schemaVersion),
      signal: AbortSignal.timeout(this.timeout),
    };

    const response = await fetch(url.toString(), options);

    if (response.status === 200) {
      return;
    } else if (
      response.status === ERRORCODES.REQUEST_ERROR ||
      response.status === ERRORCODES.FORBIDDEN ||
      response.status === ERRORCODES.NOT_FOUND ||
      response.status === ERRORCODES.SERVER_ERROR
    ) {
      throw new DocumentError(`${response.status} id: ${id}`, response.status);
    } else {
      throw new DocumentError(
        `Server returned ${response.status} for id: ${id}`,
        ERRORCODES.SERVER_ERROR,
      );
    }
  }

  getProject(documentId: string, projectId: string) {
    validateDocumentId(documentId);
    const url = new URL(
      Url.resolve(this.server, `${v3ApiPath}/projects/${projectId}`),
    );
    const credentials = this.getToken();

    const headers = credentials
      ? { authorization: `Bearer ${credentials}` }
      : undefined;

    const options: RequestInit = {
      method: "GET",
      mode: "cors",
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };

    /*** Triggering notifications requires providing both the ID and the name of the assigned project.
     * To avoid having to specify these details each time the trigger is sent, we retrieve the missing project name
     * when the document is loaded. The request should not cause the application to crash.
     * */
    return fetch(url, options as RequestInit)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        return Promise.resolve();
      })
      .catch(() => {
        return Promise.resolve();
      });
  }

  //TODO: This needs to be wired to correct endpoint, for now check if existing projects is assignable by the user - that requires the admin role
  myProjects() {
    const url = new URL(Url.resolve(this.server, `${v3ApiPath}/projects/my`));
    const credentials = this.getToken();

    const headers = credentials
      ? { authorization: `Bearer ${credentials}` }
      : undefined;

    const options: RequestInit = {
      method: "GET",
      mode: "cors",
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };
    return fetch(url, options as RequestInit).then((response) => {
      if (response.status === 200) {
        return response.json();
      } else {
        // This request should not cause the application to crash - however this should really be handled by the caller
        return Promise.resolve();
      }
    });
  }

  roles(id: string) {
    validateDocumentId(id);
    const url = Url.resolve(this.server, `${v3ApiPath}/workspace/${id}/roles`);

    const credentials = this.getToken();
    const headers = credentials
      ? { authorization: `Bearer ${credentials}` }
      : undefined;

    const options: RequestInit = {
      method: "GET",
      mode: "cors",
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };

    return fetch(url, options).then(handleResponse(id));
  }
}

function validateDocumentId(id: string) {
  if (!id || typeof id !== "string")
    throw new DocumentError("Missing 'id'", ERRORCODES.MISSING_DATA);
}

function isGetAutoSubscribeToContainerResponse(
  value: unknown,
): value is GetAutoSubscribeToContainerResponse {
  if (!value || typeof value !== "object") {
    return false;
  }

  const response = value as Partial<GetAutoSubscribeToContainerResponse>;
  return (
    typeof response.containerId === "string" &&
    typeof response.containerName === "string"
  );
}

// TODO: Refactor the response handling in this file.
//       All functions should use a type guard for response objects and there should be response types in the first place.
function handleResponse(workspaceId: string) {
  return (response: Response) => {
    if (response.status === 200) {
      return response.json();
    } else if (
      response.status === ERRORCODES.REQUEST_ERROR ||
      response.status === ERRORCODES.NOT_AUTHORIZED ||
      response.status === ERRORCODES.FORBIDDEN ||
      response.status === ERRORCODES.NOT_FOUND ||
      response.status === ERRORCODES.PRECONDITION_FAILED ||
      response.status === ERRORCODES.SERVER_ERROR
    ) {
      return Promise.reject(
        new DocumentError(
          `${response.status} id: ${workspaceId}`,
          response.status,
        ),
      );
    } else {
      return Promise.reject(
        new DocumentError(
          `Server returned ${response.status} for id: ${workspaceId}`,
          ERRORCODES.SERVER_ERROR,
        ),
      );
    }
  };
}
